import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { ConnectorStatus } from '../common/enums/connectorStatus';
import { Connector } from '../pages/Station/types';

const POLLING_INTERVAL_MS = 5000;

/**
 * The two screens that drive themselves. `useChargingSessionPolling` owns them,
 * so this stands down rather than doubling the request rate.
 *
 * Everything else sits still until the charger says something and therefore
 * polls — listed as an exclusion rather than an allowlist because the kWh form
 * is the `default` branch of the station page's status switch: it renders on
 * Preparing, but equally on SuspendedEV, SuspendedEVSE, Reserved, Unavailable
 * and Faulted. An allowlist of Available + Preparing left a driver entering
 * kWh on any of those staring at a screen that never moved again.
 */
const SELF_DRIVING_STATUSES: string[] = [
  ConnectorStatus.Charging,
  ConnectorStatus.Finishing,
];

interface UseConnectorStatusPollingProps {
  stationId?: string;
  /** `false` before the first load, `null` on the picker. */
  chosenConnector: Connector | null | false;
  /**
   * Second argument is the bay being polled for, so the resync reconciles
   * *this* screen rather than whichever charge the station happens to have
   * running — without it a driver waiting on the free bay was pulled back to
   * their own charge on the other one every beat.
   */
  onResync: (stationId: string, connectorId?: string) => Promise<void>;
}

/**
 * Keeps connector statuses fresh from REST while the driver is waiting on the
 * charger.
 *
 * Those screens are driven by `connectorStatus` frames over the client socket,
 * and the socket can miss one — it drops on the Cloud Run request timeout, and
 * a charger can be slow to report. On a station with a single connector the
 * driver rarely notices; on one with two or more they plug in and the page goes
 * on telling them to plug in, because nothing else re-reads the status.
 *
 * Once charging starts `useChargingSessionPolling` owns the screen, so this
 * stands down rather than doubling the request rate. Every other screen polls,
 * including the kWh form on a connector the charger is reporting as suspended,
 * reserved, unavailable or faulted — those recover only when the charger says
 * so, and nothing else on the page is listening.
 */
export const useConnectorStatusPolling = ({
  stationId,
  chosenConnector,
  onResync,
}: UseConnectorStatusPollingProps): void => {
  const onResyncRef = useRef(onResync);
  onResyncRef.current = onResync;

  // Read through a ref: the bay can change without restarting the interval.
  const connectorIdRef = useRef<string | undefined>(undefined);
  connectorIdRef.current = chosenConnector
    ? String(chosenConnector.connectorId)
    : undefined;

  const isWaiting =
    !!stationId &&
    chosenConnector !== false &&
    (chosenConnector === null ||
      !SELF_DRIVING_STATUSES.includes(chosenConnector.status as string));

  useEffect(() => {
    if (!isWaiting || !stationId) return;

    // A slow snapshot must not queue up behind itself: the next beat is skipped
    // rather than stacked, so a struggling connection gets one request at a time.
    let inFlight = false;

    const resync = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        await onResyncRef.current(stationId, connectorIdRef.current);
      } catch (error) {
        Sentry.captureException({
          message: '[Station][connectorStatusPolling]',
          error,
        });
      } finally {
        inFlight = false;
      }
    };

    const intervalId = window.setInterval(resync, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isWaiting, stationId]);
};
